// LT-181: typed client for /calendars and /booking-pages admin REST APIs.
import axios from 'axios';
import { BookingPage, BookingPageCreate, BookingPageUpdate, Calendar } from '../types/calendar';

const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export const calendarsAPI = {
  list: async (filters?: { owner_user_id?: string; is_active?: boolean }): Promise<Calendar[]> => {
    const params: Record<string, string | boolean> = {};
    if (filters?.owner_user_id) params.owner_user_id = filters.owner_user_id;
    if (typeof filters?.is_active === 'boolean') params.is_active = filters.is_active;
    const r = await client.get<Calendar[]>('/calendars/', { params });
    return r.data;
  },
  get: async (id: string): Promise<Calendar> => {
    const r = await client.get<Calendar>(`/calendars/${id}`);
    return r.data;
  },
  update: async (id: string, patch: Partial<Pick<Calendar, 'name' | 'type' | 'timezone' | 'is_active'>>): Promise<Calendar> => {
    const r = await client.patch<Calendar>(`/calendars/${id}`, patch);
    return r.data;
  },
  disconnect: async (id: string): Promise<void> => {
    await client.delete(`/calendars/${id}`);
  },
  startGoogleOAuth: async (): Promise<{ authorize_url: string }> => {
    const r = await client.post<{ authorize_url: string }>('/oauth/google/connect');
    return r.data;
  },
  startMicrosoftOAuth: async (): Promise<{ authorize_url: string }> => {
    const r = await client.post<{ authorize_url: string }>('/oauth/microsoft/connect');
    return r.data;
  },
};

export const bookingPagesAPI = {
  list: async (filters?: { is_active?: boolean; calendar_id?: string }): Promise<BookingPage[]> => {
    const params: Record<string, string | boolean> = {};
    if (typeof filters?.is_active === 'boolean') params.is_active = filters.is_active;
    if (filters?.calendar_id) params.calendar_id = filters.calendar_id;
    const r = await client.get<BookingPage[]>('/booking-pages/', { params });
    return r.data;
  },
  get: async (id: string): Promise<BookingPage> => {
    const r = await client.get<BookingPage>(`/booking-pages/${id}`);
    return r.data;
  },
  create: async (payload: BookingPageCreate): Promise<BookingPage> => {
    const r = await client.post<BookingPage>('/booking-pages/', payload);
    return r.data;
  },
  update: async (id: string, patch: BookingPageUpdate): Promise<BookingPage> => {
    const r = await client.patch<BookingPage>(`/booking-pages/${id}`, patch);
    return r.data;
  },
  remove: async (id: string): Promise<void> => {
    await client.delete(`/booking-pages/${id}`);
  },
};
